import React from 'react';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { MarcaService } from '../services/marcaService';
import { type MarcaListItem } from '../types/marca';

export interface MarcaSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter marcas */
  filter?: (marca: MarcaListItem) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * MarcaSelect Component
 *
 * A reusable dropdown component that displays marcas from the marcas service.
 * Features:
 * - Automatic data loading from marca service
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const MarcaSelect: React.FC<MarcaSelectProps> = ({
  value,
  onChange,
  label = 'Marca',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const [marcas, setMarcas] = React.useState<MarcaListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch marcas on component mount
  const fetchMarcas = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await MarcaService.getMarcas();
      setMarcas(data);
    } catch (error) {
      console.error('Error fetching marcas:', error);
      setMarcas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchMarcas();
  }, [fetchMarcas]);

  // Filter marcas based on props
  const filteredMarcas = React.useMemo(() => {
    let filtered = marcas;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [marcas, filter]);

  // Convert marcas to select options
  const options = React.useMemo(() => {
    return filteredMarcas.map((marca) => ({
      value: marca.idMarca,
      label: marca.descripcion,
    }));
  }, [filteredMarcas]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default MarcaSelect;